import stationDataConfigApi from '@/api/config/stationDataConfig'
import { queryKeys } from '@/constant/common'
import { APIResponseList } from '@/types/common'
import { Station } from '@/types/station'
import type {
  StationServerFilters,
  StationServerSort,
} from '@/features/config/pages/StationTableView/server-filters'
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'

type StationInfiniteQueryKey = [
  string,
  number | null,
  string | undefined,
  number,
  string,
  string,
]

/** What /config pages by. The guided setup asks for the list in one go. */
const DEFAULT_PAGE_SIZE = 20

/**
 * A page of the list, stamped with when it was requested. A station create
 * whose outcome is unknown is decided only by a read that started after it
 * (see StationTableView/station-write-registry.ts).
 */
export type StationListPage = APIResponseList<Station> & { requestedAt?: number }

export const toStationRow = (item: any) => ({
  id: item.id,
  controller: item.controller,
  decoderType: item.decoderType,
  state: item.state,
  plant: item.plant,
  emitter: item.emitter,
  etRuntime: item.etRuntime,
  interfaceId: item.interfaceId,
  index: item.index,
  address: item.address,
  decoderId: item.decoderId,
  // The sub-controller on a control hub, where two can share an address.
  twiId: item.twiId ?? null,
  type: item.type,
  soil: item.soil,
  errorCode: item.errorCode,
  errorName: item.errorName,
  waterRequired: item.waterRequired,
  surfaceAccumulation: item.surfaceAccumulation,
  precipitationRate: item.precipitationRate,
  expectedFlow: item.expectedFlow,
  landscapeFactor: item.landscapeFactor,
  etZone: item.etZone,
  rtDayOverride: item.rtDayOverride,
  pipe: item.pipe,
  defaultRunTime: item.defaultRunTime,
  observedFlow: item.observedFlow,
  plantAvailWater: item.plantAvailWater,
  stateName: item.stateName,
  description: item.description,
  subArea: item.subArea,
  priority: item.priority,
  floorId: item.floorId,
  locationId: item.locationId,
  subAreaId: item.subAreaId,
  plantId: item.plantId,
  soilId: item.soilId,
  emitterId: item.emitterId,
  stationType: item.stationType,
  pictureUrl: item.pictureUrl,
  pictureFileName: item.pictureFileName,
  grsCoordinatedLatitude: item.grsCoordinatedLatitude,
  grsCoordinatedLongitude: item.grsCoordinatedLongitude,
})

export default function useSearchStationInfinityList(
  interfaceId: number | null,
  queryOptions?: UseInfiniteQueryOptions<
    APIResponseList<Station>,
    unknown,
    APIResponseList<Station>,
    APIResponseList<Station>,
    StationInfiniteQueryKey
  >,
  keyword?: string,
  /**
   * SCRUM-1032 — 0 asks the endpoint for every station at once.
   *
   * The guided setup needs the whole list, not a page of it: the station rows
   * are drafted from the decoders that have no station yet, so a partial list
   * would draft a second row for every station below the fold.
   */
  pageSize: number = DEFAULT_PAGE_SIZE,
  /**
   * SCRUM-1071 column filters, already mapped to the endpoint's repeatable id
   * parameters. `query-string` sends an array as `AreaIds=1&AreaIds=6`, which
   * is the form the endpoint reads.
   */
  serverFilters?: StationServerFilters,
  /**
   * `SortBy` / `SortDirection` when the sorted column is one the endpoint
   * knows. `null` leaves the ordering to the endpoint's default and the sorting
   * to the grid — which then needs the list whole to be right.
   */
  serverSort?: StationServerSort | null,
): UseInfiniteQueryResult<StationListPage, unknown> {
  const isEnabled =
    (queryOptions?.enabled ?? true) &&
    interfaceId !== null &&
    interfaceId !== undefined

  return useInfiniteQuery<
    StationListPage,
    unknown,
    StationListPage,
    StationInfiniteQueryKey
  >(
    // The filters belong in the key: two different filters are two different
    // lists, and an infinite query that reuses a key keeps the pages it already
    // holds — the old rows would stay on screen under the new filter.
    [
      queryKeys.stations,
      interfaceId,
      keyword,
      pageSize,
      JSON.stringify(serverFilters ?? {}),
      JSON.stringify(serverSort ?? {}),
    ],

    async ({ pageParam = 1 }) => {
      const interfaceIdNumber = interfaceId as number
      const requestedAt = Date.now()
      const { data } = await stationDataConfigApi.getAll({
        interfaceId: interfaceIdNumber,
        PageSize: pageSize,
        PageIndex: pageParam,
        ...(keyword ? { keyword } : {}),
        ...(serverFilters ?? {}),
        ...(serverSort ?? {}),
      })

      if (!data) {
        return {
          list: [],
          page: 1,
          size: 0,
          totalCount: 0,
          totalPage: 1,
          unpinnedStations: 0,
          requestedAt,
        }
      }

      const stationList = data.data?.map(toStationRow) ?? []

      return {
        list: stationList,
        page: data.page,
        size: data.size,
        sort: data.sort,
        totalCount: data.totalCount,
        totalPage: data.totalPage,
        unpinnedStations: data.unpinnedStations,
        requestedAt,
      }
    },

    {
      ...queryOptions,
      enabled: isEnabled,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined
        // `PageSize: 0` already answered with every station. The endpoint still
        // reports a `totalPage` counted in pages of its own default size, so
        // this read "more to come" and asked for page 2 — which, being another
        // PageSize 0 request, returned the whole list a second time. Every
        // station then had a twin, and the grid flagged all of them as sharing
        // a decoder.
        if (pageSize === 0) return undefined
        return lastPage.page < lastPage.totalPage
          ? lastPage.page + 1
          : undefined
      },
    },
  )
}
